export function parseCustomizationImageIds(raw) {
  if (raw == null || String(raw).trim() === '') return [];
  try {
    const v = JSON.parse(String(raw));
    return Array.isArray(v) ? v.filter(Boolean) : [];
  } catch {
    return [];
  }
}

export function metaValueMatchesOrderId(meta, orderId) {
  if (!meta || orderId == null) return false;
  const v = meta.value;
  if (typeof v === 'object' && v !== null && v.id != null) {
    return String(v.id) === String(orderId);
  }
  return String(v) === String(orderId);
}

function historyMetaMatchesKnownEvents(meta, historyEventIdSet) {
  if (!meta || !historyEventIdSet?.size) return false;
  const v = meta.value;
  if (typeof v === 'object' && v !== null && v.id != null && String(v.id).trim() !== '') {
    return historyEventIdSet.has(String(v.id));
  }
  if (v != null && v !== '') {
    return historyEventIdSet.has(String(v));
  }
  return false;
}

export function mediaLinkedToOrderCandidates(media, orderId, historyEventIdSet) {
  if (!media?.metadata?.length) return false;
  return media.metadata.some((m) => {
    if (m.key === 'order_attachment' || m.key === 'order_status_draft') {
      return metaValueMatchesOrderId(m, orderId);
    }
    if (m.key === 'order_status_history') {
      return historyMetaMatchesKnownEvents(m, historyEventIdSet);
    }
    if (m.key === 'order_gallery') {
      return metaValueMatchesOrderId(m, orderId);
    }
    return false;
  });
}

export function collectCandidateMediaIds(order, formItems, orderId, allMedias) {
  const historyEventIdSet = new Set(
    (order?.status_history || [])
      .map((h) => h?.id)
      .filter((hid) => hid != null && String(hid).trim() !== '')
      .map((hid) => String(hid)),
  );
  const ids = new Set();
  for (const m of allMedias) {
    if (mediaLinkedToOrderCandidates(m, orderId, historyEventIdSet)) {
      ids.add(m.id);
    }
  }
  for (const f of order?.customization_fields || []) {
    if (f.value_type === 'image') {
      parseCustomizationImageIds(f.value).forEach((id) => ids.add(id));
    }
  }
  for (const line of order?.lines || []) {
    for (const item of line.items || []) {
      for (const f of item.customization_fields || []) {
        if (f.value_type === 'image') {
          parseCustomizationImageIds(f.value).forEach((id) => ids.add(id));
        }
      }
    }
  }
  for (const row of formItems || []) {
    for (const f of row.customization_fields || []) {
      if (f.value_type === 'image') {
        const raw = (row.customizationValues || {})[f.key];
        parseCustomizationImageIds(raw).forEach((id) => ids.add(id));
      }
    }
  }
  return ids;
}

export function collectLineItemImageMediaIds(formItem) {
  const ids = new Set();
  for (const f of formItem?.customization_fields || []) {
    if (f.value_type === 'image') {
      const raw = (formItem.customizationValues || {})[f.key];
      parseCustomizationImageIds(raw).forEach((id) => ids.add(id));
    }
  }
  return ids;
}

export function collectRegisterLineItemImageCandidateIds(order, formItems, orderId, formItem, allMedias) {
  const ids = collectCandidateMediaIds(order, formItems, orderId, allMedias);
  collectLineItemImageMediaIds(formItem).forEach((id) => ids.add(id));
  return ids;
}

export function buildOrderMediaCollectionKey(order, formItems, orderId, refreshKey = 0) {
  if (!orderId) return '';
  const historyIds = (order?.status_history || [])
    .map((h) => h?.id)
    .filter((hid) => hid != null && String(hid).trim() !== '')
    .map((hid) => String(hid))
    .sort();
  const orderImageValues = (order?.customization_fields || [])
    .filter((f) => f.value_type === 'image')
    .map((f) => String(f.value || ''));
  const lineImageValues = (order?.lines || []).flatMap((line) =>
    (line.items || []).flatMap((item) =>
      (item.customization_fields || [])
        .filter((f) => f.value_type === 'image')
        .map((f) => String(f.value || '')),
    ),
  );
  const formImageValues = (formItems || []).flatMap((row) =>
    (row.customization_fields || [])
      .filter((f) => f.value_type === 'image')
      .map((f) => String((row.customizationValues || {})[f.key] || '')),
  );
  return JSON.stringify({
    orderId: String(orderId),
    refreshKey,
    historyIds,
    orderImageValues,
    lineImageValues,
    formImageValues,
  });
}
